import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { translateMonthNames } from "src/utils/translateMonthNames";
import { Star } from "../../assets/icons/icons";

// ОТЗЫВ НА САЙТЕ — В ДВУХ ВИДАХ.
//
//  1. Написанный на сайте: имя, звёзды, текст, иногда фото из поездки.
//  2. Скриншот: переписка в WhatsApp, сторис, карточка в 2GIS — их загружает
//     менеджер через админку. Текста и оценки у такого отзыва нет, весь отзыв
//     и есть картинка.
//
// Второй вид нельзя рисовать шаблоном первого: кружок с «?» вместо имени,
// пять пустых звёзд («ноль из пяти») и мелкая плитка 80×80, на которой ничего
// не прочитать. Поэтому скриншот показывается крупно и без обвязки.
export default function Review({ name, date, stars, comment, photos = [] }) {
  const { t, i18n } = useTranslation();
  const formattedDate = translateMonthNames(date, i18n);
  // Индекс фото, открытого на весь экран (null — ничего не открыто)
  const [zoomed, setZoomed] = useState(null);

  const src = (url) => `${process.env.REACT_APP_BASE_API}${url}`;
  const isScreenshot = !comment?.trim() && photos.length > 0;

  return (
    <div className="py-8 sm:py-10">
      {!isScreenshot && (
        <div className="flex items-center">
          <div className="h-11 w-11 flex-shrink-0 rounded-full bg-kj-card border border-kj-line flex items-center justify-center text-kj-gold font-extrabold uppercase">
            {(name || "?").trim().charAt(0)}
          </div>
          <div className="ml-4 min-w-0">
            <h4 className="text-base font-semibold text-kj-ink truncate">
              {name}
            </h4>
            {stars > 0 && (
              <div className="mt-1 flex items-center">
                {[0, 1, 2, 3, 4].map((rating, index) => (
                  <Star
                    key={index}
                    className={clsx(
                      stars > rating ? "fill-kj-gold" : "fill-neutral-600",
                      "h-5 w-5 flex-shrink-0"
                    )}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {comment ? (
        <div
          className="mt-4 space-y-6 text-base italic text-neutral-200 leading-relaxed break-words"
          dangerouslySetInnerHTML={{ __html: comment }}
        />
      ) : null}

      {photos.length > 0 && (
        <div
          className={clsx(
            "flex flex-wrap gap-3",
            // Скриншот — сам отзыв, поэтому во всю ширину колонки. Фото к
            // тексту — приложение к нему, им хватает плитки.
            isScreenshot ? "mt-0" : "mt-4"
          )}
        >
          {photos.map((url, i) => (
            <button
              key={`${url}-${i}`}
              type="button"
              onClick={() => setZoomed(i)}
              aria-label={t("reviews.openScreenshot")}
              className={clsx(
                "rounded-lg overflow-hidden border border-kj-line hover:border-kj-gold transition-colors",
                isScreenshot
                  ? "w-full max-w-sm"
                  : "h-20 w-20 sm:h-24 sm:w-24"
              )}
            >
              <img
                src={src(url)}
                alt={isScreenshot ? t("reviews.screenshotAlt") : ""}
                loading="lazy"
                className={isScreenshot ? "w-full h-auto" : "h-full w-full object-cover"}
              />
            </button>
          ))}
        </div>
      )}

      {/* У обычного отзыва имя стоит в шапке карточки, второй раз его писать
          незачем. У скриншота шапки нет — подпись («Instagram», «WhatsApp»)
          и дата собираются здесь в одну строку. */}
      <div className="mt-4 text-sm text-kj-sub">
        {[isScreenshot ? name : "", formattedDate].filter(Boolean).join(" · ")}
      </div>

      {zoomed !== null && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoomed(null)}
          role="presentation"
        >
          <button
            type="button"
            onClick={() => setZoomed(null)}
            aria-label="Close"
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={src(photos[zoomed])}
            alt=""
            className="max-h-full max-w-full object-contain rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
